import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import tickSoundFile from '../../sounds/tick.mp3';
import { classNames } from '../../utils';
import { Segment, SpinTime, getContrastingColor } from './utils';

interface WheelProps {
  onSegmentSelected: (segment: Segment) => void;
  onSpinningStateChange?: (isSpinning: boolean) => void;
  className?: string;
}

// Defining the methods available through the ref
export interface WheelRef {
  setSegments: (segments: Segment[]) => void;
  spin: () => void;
  setSpinTime: (spinTime: SpinTime) => void;
  toggleSound: (volumeOn: boolean) => void;
}

const fontCache = new Map<string, string>();

function getContextFontStr(
  ctx: CanvasRenderingContext2D,
  segments: Segment[],
  maxTextWidth: number,
  maxFontSize: number,
  minFontSize: number
): string {
  let fontSize = maxFontSize;
  const typefaceString = 'Helvetica, Arial, sans-serif';

  const cacheKey = segments.map((s) => s.label).join();

  if (!fontCache.has(cacheKey)) {
    for (const segment of segments) {
      ctx.font = `${fontSize}px ${typefaceString}`;
      let textWidth = ctx.measureText(segment.label).width;
      while (textWidth > maxTextWidth && fontSize >= minFontSize) {
        fontSize--;
        ctx.font = `${fontSize}px ${typefaceString}`;
        textWidth = ctx.measureText(segment.label).width;
      }
    }

    if (fontCache.size > 1000) {
      fontCache.clear();
    }
    fontCache.set(cacheKey, `${fontSize}px ${typefaceString}`);
  }

  return fontCache.get(cacheKey) as string;
}

const segmentLabelCache = new Map<string, string>();

function getSegmentTruncatedLabel(
  ctx: CanvasRenderingContext2D,
  contextFontStr: string,
  fullLabel: string,
  maxTextWidth: number
): string {
  const cacheKey = `${contextFontStr}${fullLabel}${maxTextWidth}`;

  if (!segmentLabelCache.has(cacheKey)) {
    let label = fullLabel;
    if (ctx.measureText(label).width > maxTextWidth) {
      while (ctx.measureText(label + '…').width > maxTextWidth) {
        label = label.slice(0, -1);
      }
      label += '…';
    }

    if (segmentLabelCache.size > 1000) {
      segmentLabelCache.clear();
    }
    segmentLabelCache.set(cacheKey, label);
  }

  return segmentLabelCache.get(cacheKey) || fullLabel;
}

const Wheel = React.forwardRef(
  (props: WheelProps, ref: ForwardedRef<WheelRef>) => {
    const audioContextRef = useRef<AudioContext | null>(null);
    const tickSoundBufferRef = useRef<AudioBuffer | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);
    const defaultVolume = 0.2;

    useEffect(() => {
      // Clean up on component unmount
      return () => {
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== 'closed'
        ) {
          audioContextRef.current.close();
        }
      };
    }, []);

    async function loadTickSound() {
      try {
        // Initialize AudioContext
        const audioContext = new window.AudioContext();
        audioContextRef.current = audioContext;

        // Create a GainNode for volume control
        const gainNode = audioContext.createGain();
        gainNode.gain.value = defaultVolume;
        gainNodeRef.current = gainNode;

        // Fetch the audio file and decode it into an AudioBuffer
        const response = await fetch(tickSoundFile);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        tickSoundBufferRef.current = audioBuffer;

        // Connect the GainNode to the audio context destination
        gainNode.connect(audioContext.destination);
      } catch (error) {
        console.error('Error loading tick sound:', error);
      }
    }

    const playTickSound = useCallback(async () => {
      // If the audio context is not initialized, initialize it
      if (!audioContextRef.current) {
        await loadTickSound();
      }

      if (
        audioContextRef.current &&
        tickSoundBufferRef.current &&
        gainNodeRef.current
      ) {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = tickSoundBufferRef.current;
        source.connect(gainNodeRef.current);
        source.start(0);
      }
    }, []);

    const segmentsRef = useRef<Segment[]>([]);
    const userHasInteracted = useRef(false);

    const { onSegmentSelected, onSpinningStateChange } = props;

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const slowSpinVelocityRadPerS = 0.1;
    const stopVelocityRadPerS = 0.02;

    const defaultAccelerationPhaseAngularAccelerationRadPerS2 = 8;
    const accelerationPhaseAngularAccelerationRadPerS2Ref = useRef(
      defaultAccelerationPhaseAngularAccelerationRadPerS2
    );

    const defaultFrictionDecelerationFactorPerS = 0.7; // x fraction of velocity per second is lost due to friction
    // The friction factor that should be used on a normal spin (not constant demo spin)
    const spinFrictionDecelerationFactorPerSRef = useRef(
      defaultFrictionDecelerationFactorPerS
    );
    // The friction factor currently in use
    const frictionDecelerationFactorPerSRef = useRef(
      defaultFrictionDecelerationFactorPerS
    );

    const angleRadRef = useRef(0); // Unit: radians
    const spinVelocityRadPerSRef = useRef(0); // Unit: radians per second
    const spinTargetVelocityRadPerSRef = useRef(0); // Unit: radians per second
    const acceleratingRef = useRef(false);
    const indicatorAngleRadRef = useRef(0); // Unit: radians
    const previousIndicatorAngleRadRef = useRef(0); // Unit: radians
    const previousStudIndexRef = useRef(-1);

    useEffect(() => {
      drawWheel(true);
      props.onSpinningStateChange?.(false);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Dependency array is empty, meaning this effect runs once on mount

    const drawWheel = useCallback(
      (initialDraw = false) => {
        // Initialization similar to before
        let drawAngleRad = angleRadRef.current;

        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const numSegments = segmentsRef.current.length;
        const angleStepRad = (2 * Math.PI) / numSegments;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(canvas.width, canvas.height) / 2 - 50; // Adjust radius to fit canvas with margin

        let maxFontSize = 34; // Initial, maximum, font size
        if (numSegments > 30) {
          maxFontSize = 30;
        }
        if (numSegments > 40) {
          maxFontSize = 24;
        }
        if (numSegments > 60) {
          maxFontSize = 20;
        }

        const minFontSize = 16; // Minimum font size
        const innerMargin = 70;
        const maxTextWidth = radius * 0.95 - innerMargin; // Adjust as necessary for your layout
        // Set font size and font family for segment labels
        const contextFontStr = getContextFontStr(
          ctx,
          segmentsRef.current,
          maxTextWidth,
          maxFontSize,
          minFontSize
        );
        ctx.font = contextFontStr;

        if (numSegments === 0) {
          ctx.beginPath();
          ctx.fillStyle = '#d3d3d3'; // Empty wheel color
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.fill();
        } else {
          segmentsRef.current.forEach((segment) => {
            ctx.beginPath();
            ctx.fillStyle = segment.color;
            ctx.moveTo(centerX, centerY);
            ctx.arc(
              centerX,
              centerY,
              radius,
              drawAngleRad,
              drawAngleRad + angleStepRad
            );
            ctx.lineTo(centerX, centerY);
            ctx.fill();

            // Draw medium-thick borders between segments
            ctx.lineWidth = 5;
            ctx.strokeStyle = '#fff';
            ctx.stroke();

            // Text color
            ctx.fillStyle = getContrastingColor(segment.color);
            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate(drawAngleRad + angleStepRad / 2);

            // Truncate text if necessary and add ellipsis
            let text = getSegmentTruncatedLabel(
              ctx,
              contextFontStr,
              segment.label,
              maxTextWidth
            );

            ctx.textAlign = 'right'; // Right-align text
            ctx.textBaseline = 'middle'; // Vertically center text

            ctx.fillText(text, maxTextWidth + innerMargin, 0); // Adjust position as needed
            ctx.restore();

            drawAngleRad += angleStepRad;
          });
        }

        // Save the canvas state before adding the shadow
        ctx.save();

        // Set shadow properties for the entire wheel
        ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 8;

        // Draw the wheel border (this will include the shadow)
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 6, 0, 2 * Math.PI);
        ctx.lineWidth = 15;
        ctx.strokeStyle = '#fff';
        ctx.stroke();

        // Restore the canvas state to avoid shadow on the indicator
        ctx.restore();

        ctx.save();
        // Set shadow properties for the entire wheel
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 3;

        // Draw small circles at segment borders
        let numStuds = numSegments;
        if (numStuds < 3) {
          numStuds = 20;
        } else if (numStuds < 3) {
          numStuds *= 6;
        } else if (numStuds < 4) {
          numStuds *= 5;
        } else if (numStuds < 6) {
          numStuds *= 4;
        } else if (numStuds < 11) {
          numStuds *= 3;
        } else if (numStuds < 21) {
          numStuds *= 2;
        }
        const studAngleStep = (2 * Math.PI) / numStuds;

        for (let i = 0; i < numStuds; i++) {
          const borderAngle = angleRadRef.current + i * studAngleStep;
          const circleX = centerX + (radius + 7) * Math.cos(borderAngle);
          const circleY = centerY + (radius + 7) * Math.sin(borderAngle);
          ctx.beginPath();
          ctx.arc(circleX, circleY, 5, 0, 2 * Math.PI);
          ctx.fillStyle = '#6E6E6E';
          ctx.fill();
        }
        ctx.restore();

        // Indicator logic
        const indicatorMovementMargin = (3 * Math.PI) / 180;
        const angleWithIndicatorMargin =
          angleRadRef.current + 0.5 * indicatorMovementMargin;
        indicatorAngleRadRef.current = 0; // Nudge the indicator down

        // Check if any stud is at the moment nudging the indicator
        if (!initialDraw) {
          for (let i = 0; i < numStuds; i++) {
            let borderAngle = (i * studAngleStep) % (2 * Math.PI);

            /**
             * Check if the indicator is within the margin of this border. The margin applies only when
             * the border is below the normal level of the indicator.
             */
            if (
              Math.abs(angleWithIndicatorMargin - borderAngle) <
                indicatorMovementMargin &&
              angleWithIndicatorMargin > borderAngle
            ) {
              const newIndicatorAngleRad =
                (angleWithIndicatorMargin - borderAngle) * 5;
              // Round very small values to zero
              if (newIndicatorAngleRad > 0.04) {
                indicatorAngleRadRef.current = newIndicatorAngleRad;
              }
              break;
            }
          }
        }

        const thresholdSpeedRadPerS = 1;

        // Check if a stud has passed the rightmost position at 0.5*pi radians
        if (spinVelocityRadPerSRef.current > thresholdSpeedRadPerS) {
          const currentStudIndex = Math.floor(
            angleRadRef.current / studAngleStep
          );
          if (currentStudIndex !== previousStudIndexRef.current) {
            playTickSound();
            previousStudIndexRef.current = currentStudIndex;
          }
        } else {
          if (
            indicatorAngleRadRef.current === 0 &&
            previousIndicatorAngleRadRef.current > 0 &&
            spinVelocityRadPerSRef.current > 0 &&
            userHasInteracted.current
          ) {
            playTickSound();
          }
          previousIndicatorAngleRadRef.current = indicatorAngleRadRef.current;
        }

        // Draw the shadowed triangle
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 13;
        ctx.shadowOffsetX = -2;
        ctx.shadowOffsetY = 3;
        ctx.beginPath();
        ctx.translate(centerX + radius + 12, centerY); // Move to the tip of the triangle
        ctx.rotate(-indicatorAngleRadRef.current);
        ctx.moveTo(27, -20); // Start just outside the wheel (increased size)
        ctx.lineTo(27, 20); // Top point of the triangle (increased size)
        ctx.lineTo(-27, 0); // Bottom point of the triangle (increased size)
        ctx.closePath();
        ctx.fillStyle = '#8ecc66';
        ctx.fill();
        ctx.lineWidth = 7;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.restore();

        // Draw the final triangle with fill and stroke
        ctx.save();
        ctx.beginPath();
        ctx.translate(centerX + radius + 12, centerY); // Move to the tip of the triangle
        ctx.rotate(-indicatorAngleRadRef.current);
        ctx.moveTo(27, -20); // Start just outside the wheel (increased size)
        ctx.lineTo(27, 20); // Top point of the triangle (increased size)
        ctx.lineTo(-27, 0); // Bottom point of the triangle (increased size)
        ctx.closePath();
        ctx.fillStyle = '#8ecc66';
        ctx.fill();
        ctx.lineWidth = 7;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.restore();
      },
      [playTickSound]
    );

    const findWinningSegment = useCallback((): Segment => {
      const numSegments = segmentsRef.current.length;
      const anglePerSegment = (2 * Math.PI) / numSegments;
      let smallestAngleDifference = Infinity;
      let winningIndex = 0;

      for (let i = 0; i < numSegments; i++) {
        const segmentCenterAngle =
          (i * anglePerSegment + anglePerSegment + angleRadRef.current) %
          (2 * Math.PI);
        const difference = Math.abs(segmentCenterAngle + Math.PI); // Indicator is to the right of the center

        if (difference < smallestAngleDifference) {
          smallestAngleDifference = difference;
          winningIndex = i;
        }
      }

      return segmentsRef.current[winningIndex];
    }, []);

    const animationFrameRef = useRef<number>();
    const lastTimestampRef = useRef<number>();

    const animateSpin = useCallback(() => {
      animationFrameRef.current = requestAnimationFrame((timestamp) => {
        let timeDiffMs = 16.67;
        if (!lastTimestampRef.current) {
          lastTimestampRef.current = timestamp;
        } else {
          timeDiffMs = timestamp - lastTimestampRef.current; // 16.67 ms for 60 Hz, 8.33 ms for 120 Hz
          lastTimestampRef.current = timestamp;
        }
        const timeDiffS = timeDiffMs / 1000;

        if (acceleratingRef.current) {
          spinVelocityRadPerSRef.current +=
            accelerationPhaseAngularAccelerationRadPerS2Ref.current * timeDiffS;
          if (
            spinVelocityRadPerSRef.current >=
            spinTargetVelocityRadPerSRef.current
          ) {
            acceleratingRef.current = false;
          }
        } else if (spinVelocityRadPerSRef.current > stopVelocityRadPerS) {
          spinVelocityRadPerSRef.current *=
            1 - frictionDecelerationFactorPerSRef.current * timeDiffS;
        } else if (indicatorAngleRadRef.current > 0.001) {
          angleRadRef.current -=
            (indicatorAngleRadRef.current * 0.003 * timeDiffMs) / 8 +
            (0.0003 * timeDiffMs) / 8;
        } else {
          spinVelocityRadPerSRef.current = 0;
          const winningSegment = findWinningSegment();
          onSegmentSelected(winningSegment);
          onSpinningStateChange?.(false);
          return; // Stop animation when finished
        }

        angleRadRef.current += spinVelocityRadPerSRef.current * timeDiffS;
        angleRadRef.current %= 2 * Math.PI; // Normalize the angle
        drawWheel();
        animateSpin(); // Continue animation
      });
    }, [
      drawWheel,
      findWinningSegment,
      onSegmentSelected,
      onSpinningStateChange,
    ]);

    useEffect(() => {
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current); // Cleanup animation frame when the component unmounts or props change
        }
      };
    }, [animateSpin]);

    useEffect(() => {
      // Start with a constant slow spin for the demo
      frictionDecelerationFactorPerSRef.current = 0; // Remove friction for slow constant spin
      spinVelocityRadPerSRef.current = slowSpinVelocityRadPerS;
      animateSpin();
    }, [animateSpin]);

    useImperativeHandle(ref, () => ({
      spin() {
        lastTimestampRef.current = undefined;
        frictionDecelerationFactorPerSRef.current =
          spinFrictionDecelerationFactorPerSRef.current;
        userHasInteracted.current = true;
        props.onSpinningStateChange?.(true);
        spinTargetVelocityRadPerSRef.current = Math.random() * 10 + 10;
        spinVelocityRadPerSRef.current = slowSpinVelocityRadPerS;
        acceleratingRef.current = true;
        animateSpin();
      },
      setSpinTime(spinTime: SpinTime) {
        // About 11 seconds of spinning
        let newAccelerationPhaseAngularAccelerationRadPerS2 = 8;
        let newFrictionDecelerationFactorPerSRef = 0.7;

        if (spinTime === 19) {
          // About 19 seconds of spinning
          newAccelerationPhaseAngularAccelerationRadPerS2 = 4;
          newFrictionDecelerationFactorPerSRef = 0.45;
        } else if (spinTime === 6) {
          // About 6 seconds of spinning
          newAccelerationPhaseAngularAccelerationRadPerS2 = 16; // 8
          newFrictionDecelerationFactorPerSRef = 1.4; // 0.7
        }

        accelerationPhaseAngularAccelerationRadPerS2Ref.current =
          newAccelerationPhaseAngularAccelerationRadPerS2;
        spinFrictionDecelerationFactorPerSRef.current =
          newFrictionDecelerationFactorPerSRef;
      },
      toggleSound(volumeOn: boolean) {
        if (gainNodeRef.current) {
          gainNodeRef.current.gain.value = volumeOn ? defaultVolume : 0;
        }
      },
      setSegments(segments: Segment[]) {
        segmentsRef.current = segments;
        if (spinVelocityRadPerSRef.current === 0) {
          drawWheel();
        }
      },
    }));

    return (
      <canvas
        ref={canvasRef}
        width={700}
        height={700}
        className={classNames('', props.className)}
      />
    );
  }
);

export default Wheel;
